import { useMemo, useState } from 'react';
import { zodResolver } from '@hookform/resolvers/zod';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate } from 'react-router-dom';
import { z } from 'zod';

import type { ApiError } from '@/types/api.types';
import { Button } from '@components/ui/Button';
import { Input } from '@components/ui/Input';

import { useAuth } from '../../hooks/useAuth';
import { useGoogleAuth } from '../../hooks/useGoogleAuth';
import { useAuthStore } from '../../store/authStore';
import type { AccountInfo } from '../../types/auth.types';
import { AccountSelector } from '../AccountSelector/AccountSelector';

interface LoginFormValues {
  email: string;
  password: string;
}

function GoogleIcon() {
  return (
    <svg viewBox="0 0 24 24" className="h-5 w-5" aria-hidden="true">
      <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4" />
      <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853" />
      <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l3.66-2.84z" fill="#FBBC05" />
      <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335" />
    </svg>
  );
}

export function LoginForm() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const setActiveAccount = useAuthStore((s) => s.setActiveAccount);
  const [pendingAccounts, setPendingAccounts] = useState<AccountInfo[] | null>(null);

  const handleLoginSuccess = (accounts: AccountInfo[]) => {
    if (accounts.length === 1) {
      setActiveAccount(accounts[0]);
      navigate('/account', { replace: true });
    } else {
      setPendingAccounts(accounts);
    }
  };

  const handleAccountSelect = (account: AccountInfo) => {
    setActiveAccount(account);
    navigate('/account', { replace: true });
  };

  const { login, isLoggingIn, loginError } = useAuth({ onLoginSuccess: handleLoginSuccess });
  const { triggerGoogleAuth, isPending: isGooglePending, error: googleError } = useGoogleAuth({
    mode: 'login',
    onLoginSuccess: handleLoginSuccess,
  });

  const loginSchema = useMemo(
    () =>
      z.object({
        email: z.string().email(t('auth.emailInvalid')),
        password: z.string().min(8, t('auth.passwordTooShort')),
      }),
    [t],
  );

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<LoginFormValues>({
    resolver: zodResolver(loginSchema),
  });

  const onSubmit = (data: LoginFormValues) => {
    login(data);
  };

  const serverError = loginError
    ? ((loginError as ApiError).message ?? t('auth.loginFailed'))
    : (googleError ?? null);

  if (pendingAccounts) {
    return <AccountSelector accounts={pendingAccounts} onSelect={handleAccountSelect} />;
  }

  return (
    <div className="space-y-6">
      {/* Heading */}
      <div>
        <h1 className="font-display text-[2rem] font-semibold leading-tight text-foreground">
          {t('auth.welcomeBack')}
        </h1>
        <p className="mt-2 text-sm text-muted-foreground">{t('auth.welcomeBackSubtitle')}</p>
      </div>

      {/* Google — primary option */}
      <Button
        type="button"
        variant="secondary"
        size="lg"
        className="w-full justify-center"
        leftIcon={<GoogleIcon />}
        onClick={() => triggerGoogleAuth()}
        isLoading={isGooglePending}
        disabled={isLoggingIn}
      >
        {t('auth.continueWithGoogle')}
      </Button>

      {/* Divider */}
      <div className="flex items-center gap-3">
        <div className="h-px flex-1 bg-border" />
        <span className="text-xs font-medium text-muted-foreground">{t('auth.orSeparator')}</span>
        <div className="h-px flex-1 bg-border" />
      </div>

      {/* Email + password form */}
      <form onSubmit={handleSubmit(onSubmit)} noValidate className="space-y-4">
        {serverError && (
          <div role="alert" className="rounded-md bg-destructive/10 p-3 text-sm text-destructive">
            {serverError}
          </div>
        )}

        <Input
          {...register('email')}
          type="email"
          label={t('auth.emailLabel')}
          placeholder={t('auth.emailPlaceholder')}
          autoComplete="email"
          error={errors.email?.message}
        />

        <div>
          <Input
            {...register('password')}
            type="password"
            label={t('auth.passwordLabel')}
            placeholder={t('auth.passwordPlaceholder')}
            autoComplete="current-password"
            error={errors.password?.message}
          />
          <div className="mt-1.5 flex justify-end">
            <a
              href="/forgot-password"
              className="text-xs font-medium text-muted-foreground underline-offset-2 hover:text-foreground hover:underline"
            >
              {t('auth.forgotPassword')}
            </a>
          </div>
        </div>

        <Button type="submit" size="lg" isLoading={isLoggingIn} className="w-full justify-center">
          {isLoggingIn ? t('auth.signingIn') : t('auth.signIn')}
        </Button>
      </form>

      {/* Sign-up link */}
      <p className="text-center text-sm text-muted-foreground">
        {t('auth.noAccount')}{' '}
        <Link
          to="/register"
          className="font-medium text-primary underline-offset-2 hover:underline"
        >
          {t('auth.signUpLink')}
        </Link>
      </p>
    </div>
  );
}
